/**
 * UI-Action Tools
 *
 * Lets the assistant do what the toolbar / Ctrl+K command palette can
 * already do — list the actions available on the current page and run
 * one — without a bespoke "assistant API". Both surfaces read the same
 * PageActionRegistry + overlay CommandRegistry, filtered by the same
 * `unified-command` helpers CommandPalette.tsx uses, so the assistant
 * can never see or do more than what's already visibly clickable.
 *
 * `ui_run_action` declares `requiredCapabilities: ['execute']`, which
 * under the adapter's DEFAULT_PERMISSIONS is 'prompt' — every run goes
 * through the adapter's onToolConfirmation callback before executing,
 * regardless of whether the specific action is marked destructive.
 */

import type { SafeToolDefinition } from '@neuro/chat-adapter';
import type { PageActionContext } from '../../types/page-actions';
import { usePageActionRegistry } from '../../plugins/registries/page-action-registry';
import { useOverlayCommandRegistry } from '../../ui/commands/command-registry';
import { resolveEntityLink } from '../../plugins/registries/entity-link-resolver';
import {
  pageActionToUnified,
  overlayCommandToUnified,
  filterUnifiedCommands,
  type UnifiedCommand,
} from '../actions/unified-command';
import { useActivePageContextStore } from '../../plugins/registries/active-page-context-store';

const MAX_LISTED_ACTIONS = 40;

function gatherUnifiedCommands(context: PageActionContext): UnifiedCommand[] {
  const pageActions = usePageActionRegistry
    .getState()
    .getActionsForContext(context, 'command-palette');
  const overlayCommands = Array.from(useOverlayCommandRegistry.getState().commands.values());

  const unified: UnifiedCommand[] = [];
  for (const action of pageActions) {
    unified.push(pageActionToUnified(action, context, 0));
  }
  for (const cmd of overlayCommands) {
    if (cmd.isAvailable && !cmd.isAvailable(context)) continue;
    unified.push(overlayCommandToUnified(cmd, context, 0));
  }
  return unified;
}

function requireCurrentPageContext(): PageActionContext {
  const context = useActivePageContextStore.getState().context;
  if (!context) {
    throw new Error('No page is currently active — nothing to list or run yet.');
  }
  return context;
}

/**
 * Build the two UI-action tools. `navigate` is a getter so the tool always
 * uses the current react-router navigate function even though the tool
 * definitions themselves are created once per adapter lifetime.
 */
export function createUiActionTools(getNavigate: () => (path: string) => void): SafeToolDefinition[] {
  const listActions: SafeToolDefinition = {
    name: 'ui_list_actions',
    description:
      'List the actions available right now on the page the user is looking at — the ' +
      'same actions shown in the page toolbar and the Ctrl+K command palette. Call this ' +
      'before ui_run_action to find the right action id.',
    schema: {
      type: 'object',
      properties: {
        query: {
          type: 'string',
          description: 'Optional text to filter actions by label/description (fuzzy match).',
        },
      },
    },
    requiredCapabilities: ['read'],
    execute: async (args) => {
      const context = requireCurrentPageContext();
      const unified = gatherUnifiedCommands(context);
      const query = typeof args.query === 'string' ? args.query : '';
      const filtered = query ? filterUnifiedCommands(unified, query) : unified;
      return {
        pageId: context.pageId,
        actions: filtered.slice(0, MAX_LISTED_ACTIONS).map((cmd) => ({
          id: cmd.id,
          label: cmd.label,
          description: cmd.description,
          group: cmd.group,
          destructive: !!cmd.destructive,
          disabled: !!cmd.disabled,
        })),
      };
    },
  };

  const runAction: SafeToolDefinition = {
    name: 'ui_run_action',
    description:
      'Run one of the actions returned by ui_list_actions, by id, on the page the user is ' +
      'currently looking at. Always call ui_list_actions first to get a valid id. The user ' +
      'will be asked to confirm before this runs.',
    schema: {
      type: 'object',
      properties: {
        id: { type: 'string', description: 'The action id from ui_list_actions.' },
      },
      required: ['id'],
    },
    requiredCapabilities: ['execute'],
    requiresConfirmation: true,
    execute: async (args) => {
      const context = requireCurrentPageContext();
      const unified = gatherUnifiedCommands(context);
      const id = args.id as string;
      const found = unified.find((cmd) => cmd.id === id);
      if (!found) {
        throw new Error(`No action with id "${id}" on the current page. Call ui_list_actions again.`);
      }
      if (found.disabled) {
        throw new Error(`Action "${found.label}" is currently disabled.`);
      }
      await found.execute();
      return { ok: true, id: found.id, label: found.label };
    },
  };

  const navigate: SafeToolDefinition = {
    name: 'ui_navigate',
    description:
      'Navigate the app to an entity (by type + id) or an explicit path. Use this to take ' +
      'the user to something you found, e.g. via ui_list_actions or a knowledge/search tool.',
    schema: {
      type: 'object',
      properties: {
        entityType: { type: 'string', description: 'Entity type, e.g. "note", "workflow".' },
        entityId: { type: 'string', description: 'Entity id, if navigating to a specific record.' },
        path: { type: 'string', description: 'Explicit app path to navigate to, e.g. "/workflows".' },
      },
    },
    requiredCapabilities: ['read'],
    execute: async (args) => {
      const url = resolveEntityLink({
        entityType: typeof args.entityType === 'string' ? args.entityType : undefined,
        entityId: typeof args.entityId === 'string' ? args.entityId : undefined,
        explicitUrl: typeof args.path === 'string' ? args.path : undefined,
      });
      if (!url) {
        return { ok: false, reason: 'No matching route found for the given entity/path.' };
      }
      getNavigate()(url);
      return { ok: true, path: url };
    },
  };

  return [listActions, runAction, navigate];
}
